'use client';

import * as React from 'react';
import {
    TrendingUp,
    ShieldCheck,
    Users,
    Sparkles,
    ArrowUpRight,
    CircleAlert,
    CheckCircle2,
} from 'lucide-react';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { cn } from '@/lib/utils';

export function WhyMlmSoftware() {
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const highlights = [
        {
            icon: Users,
            title: 'Accurate Commissions',
            description:
                'Calculate payouts across multiple downline levels in seconds, eliminating costly manual errors.',
            gradient: 'from-emerald-500 to-teal-500',
            glow: 'shadow-emerald-500/20',
        },
        {
            icon: ShieldCheck,
            title: 'Built-in Compliance',
            description:
                'Stay aligned with FTC and DSA guidelines through role-based dashboards and audit-ready reports.',
            gradient: 'from-blue-500 to-indigo-500',
            glow: 'shadow-blue-500/20',
        },
        {
            icon: TrendingUp,
            title: 'Scalable Growth',
            description:
                'AI-powered automation handles onboarding, genealogy, and payouts as your network grows.',
            gradient: 'from-orange-500 to-pink-500',
            glow: 'shadow-orange-500/20',
        },
    ];

    const painPoints = [
        'Costly commission mistakes',
        'Frustrated distributors',
        'Compliance blind spots',
    ];

    const solutions = [
        'Instant multi-level payouts',
        'Real-time genealogy view',
        'FTC & DSA aligned by default',
    ];

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="md"
            className="relative overflow-hidden bg-white dark:bg-black"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-[10%] top-10 h-96 w-96 rounded-full bg-primary-500/10 blur-3xl" />
                <div className="absolute right-[10%] bottom-10 h-[30rem] w-[30rem] rounded-full bg-blue-500/10 blur-3xl" />
                <div
                    className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
                        backgroundSize: '32px 32px',
                    }}
                />
            </div>

            <div className="relative z-10">
                <SectionHeading
                    pill="Why MLM Software?"
                    title="Why Every MLM Company Needs Dedicated Software"
                    subtitle=""
                    isVisible={sectionRef.isVisible}
                    align="center"
                    maxWidthClass="max-w-4xl"
                    className="!mb-14"
                />

                {/* Top: Asymmetric content + stat showcase */}
                <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr] lg:gap-10 items-stretch mb-10">
                    {/* Left: Problem / Solution split card */}
                    <div
                        className={cn(
                            'relative rounded-3xl border border-gray-200/80 dark:border-gray-800/80 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm p-8 md:p-10 overflow-hidden transition-all duration-700',
                            sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                        )}
                    >
                        <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />

                        <Typography variant="p" className="mb-8 !text-[17px] md:!text-lg leading-relaxed">
                            Running a multi-level marketing business on spreadsheets or basic CRM tools often leads to{' '}
                            <span className="font-semibold text-gray-900 dark:text-white">
                                costly commission mistakes, frustrated distributors, and compliance issues
                            </span>
                            . A dedicated MLM software takes this pressure off by calculating accurate commissions across multiple downline levels at the same time.
                        </Typography>

                        <div className="grid gap-6 sm:grid-cols-2">
                            <div>
                                <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-red-500 dark:text-red-400">
                                    <CircleAlert className="h-3.5 w-3.5" />
                                    Without it
                                </div>
                                <ul className="space-y-2.5">
                                    {painPoints.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                                        >
                                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500/60" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-500 dark:text-emerald-400">
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    With it
                                </div>
                                <ul className="space-y-2.5">
                                    {solutions.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                                        >
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Right: Stat showcase card */}
                    <div
                        className={cn(
                            'relative rounded-3xl p-8 md:p-10 overflow-hidden transition-all duration-700 delay-150',
                            'bg-gradient-to-br from-primary-600 via-primary-700 to-blue-900',
                            'shadow-2xl shadow-primary-500/30',
                            sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                        )}
                    >
                        {/* Decorative blobs */}
                        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
                        <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-blue-400/20 blur-2xl" />

                        {/* Grid pattern */}
                        <div
                            className="absolute inset-0 opacity-[0.08]"
                            style={{
                                backgroundImage:
                                    'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                                backgroundSize: '40px 40px',
                            }}
                        />

                        <div className="relative">
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-sm">
                                <Sparkles className="h-3.5 w-3.5 text-yellow-300" />
                                <span className="text-xs font-semibold uppercase tracking-wider text-white">
                                    Industry Outlook
                                </span>
                            </div>

                            <div className="mb-2 text-sm font-medium text-white/80">
                                Global direct selling market
                            </div>
                            <div className="mb-3 flex items-baseline gap-2">
                                <span className="text-5xl md:text-6xl font-bold text-white tracking-tight">
                                    $186
                                </span>
                                <span className="text-2xl md:text-3xl font-bold text-white/90">
                                    Billion
                                </span>
                            </div>
                            <div className="mb-8 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/20 px-2.5 py-1 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-400/30">
                                <ArrowUpRight className="h-3 w-3" />
                                Growing year over year
                            </div>

                            <p className="text-sm leading-relaxed text-white/80">
                                The companies gaining ground are the ones investing in{' '}
                                <span className="font-semibold text-white">
                                    scalable, AI-powered MLM software
                                </span>
                                , not those still relying on manual processes.
                            </p>

                            <div className="mt-6 flex items-center gap-4 border-t border-white/15 pt-5">
                                <div className="flex -space-x-2">
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 ring-2 ring-primary-700" />
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 ring-2 ring-primary-700" />
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 ring-2 ring-primary-700" />
                                </div>
                                <div className="text-xs text-white/70">
                                    Trusted across binary, matrix & custom plans
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom: Feature highlights */}
                <div className="grid gap-5 md:grid-cols-3">
                    {highlights.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <div
                                key={item.title}
                                className={cn(
                                    'group relative rounded-2xl border border-gray-200/80 dark:border-gray-800/80 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm p-6 overflow-hidden transition-all duration-700',
                                    'hover:-translate-y-1 hover:shadow-xl',
                                    item.glow,
                                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                                )}
                                style={{ transitionDelay: `${300 + index * 120}ms` }}
                            >
                                {/* Corner glow */}
                                <div
                                    className={cn(
                                        'absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20',
                                        item.gradient
                                    )}
                                />

                                <div className="relative">
                                    <div
                                        className={cn(
                                            'mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3',
                                            item.gradient
                                        )}
                                    >
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <Typography variant="h3" className="mb-2 text-lg">
                                        {item.title}
                                    </Typography>
                                    <Typography variant="p" className="!text-sm leading-relaxed">
                                        {item.description}
                                    </Typography>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </SectionWrapper>
    );
}
